import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toaster from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';
import { BusinessAndStoreEditForm } from 'components/admin/users/BusinessAndStoreEditForm';
import Modal from 'components/common/Modal';
import DataCard from 'components/common/card/DataCard';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import IndividualValueEditForm from 'components/common/forms/IndividualValueEditForm';
import { useBusinessSearch } from 'hooks/useBusinessSearch';
import { THEME_MESSAGES } from 'utils/constants';
import passwordValidation from 'utils/passwordValidation';
import {
  assignUserToBusiness,
  editEmployeeCode,
  getIndividualUser,
  updateIndividualValueForUser,
  updateUserPassword,
} from 'api/admin/users/users';
import { useAssignInstallerToBusinessAndStore } from 'api/queries/useAssignInstallerToBusinessAndStore';
import { UserTypes } from 'constants/constants';

const IndividualUserProfile = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState();
  const [user, setUser] = useState();
  const [stores, setStores] = useState([]);
  const [modalType, setModalType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [newPassword, setNewPassword] = useState(null);
  const [updatedFormValue, setUpdatedFormValue] = useState(null);
  const [updatedFormKey, setUpdatedFormKey] = useState(null);
  const [showBusinessEditModal, setShowBusinessEditModal] = useState(false);
  const [showBusinessAndStoreEditModal, setShowBusinessAndStoreEditModal] = useState(false);
  const [businessToEdit, setBusinessToEdit] = useState(null);
  const [newBusinessId, setNewBusinessId] = useState(null);
  const [storeToEdit, setStoreToEdit] = useState(null);
  const [newStoreId, setNewStoreId] = useState(null);

  const businessEditable = useMemo(() => {
    if (user?.roles.filter((e) => e.userType === 'Business Owner').length > 0) {
      return false;
    }
    if (user?.roles.filter((e) => e.userType === 'Employee').length > 0) {
      return false;
    }
    if (user?.roles.filter((e) => e.userType === 'Super Admin').length > 0) {
      return false;
    }
    return true;
  }, [user]);

  const history = useHistory();

  const {
    data: updatedInstaller,
    isLoading: isAssignInstallerToBusinessAndStoreLoading,
    mutate: assignInstallerToBusinessAndStore,
  } = useAssignInstallerToBusinessAndStore();

  useEffect(() => {
    if (updatedInstaller) {
      setUser(updatedInstaller);
      setStores(updatedInstaller);
      setShowBusinessAndStoreEditModal(false);
    }
  }, [updatedInstaller]);

  const onSaveInstallerAssignment = async () => {
    await assignInstallerToBusinessAndStore({
      userId: user.id,
      businessId: newBusinessId || null,
      storeId: newStoreId || null,
    });
  };

  /**
   * Navigate back to the users table
   */
  const navigateToUsersTable = () => {
    history.push('/users');
  };

  /**
   * Retrieve the individual user details to display
   */
  const fetchIndividualUser = useCallback(async () => {
    try {
      setLoading(true);

      const userData = await getIndividualUser(id);
      setUser(userData.data.user);
      setStores(userData.data.stores);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toaster.error(error?.response?.data?.error || THEME_MESSAGES.somethingWrong);
      navigateToUsersTable();
    }
  }, [id]);

  const {
    businessOptions: businesses,
    isLoading: businessesLoading,
    onInputChange: businessOnInputChange,
    filterOptions: businessFilterOptions,
  } = useBusinessSearch();

  /**
   * Load user details on initialization
   */
  useEffect(() => {
    fetchIndividualUser();
  }, [fetchIndividualUser]);

  /**
   * Set the form value to be updated for the User
   *
   * @param {Any} value
   */
  const handleUserValueChange = (value) => {
    setErrorMessage(null);
    setUpdatedFormValue(value);
  };

  /**
   * Clear any errors and save changes to password in state
   */
  const handlePasswordChange = (password) => {
    setErrorMessage(null);
    setNewPassword(password);
  };

  /**
   * Clear any errors and save changes to business in state
   */
  const handleBusinessChange = (businessId) => {
    setErrorMessage(null);
    setNewBusinessId(businessId);
    setNewStoreId(null);
  };

  /**
   * Clear any errors and save changes to store in state
   */
  const handleStoreChange = (event) => {
    setErrorMessage(null);
    setNewStoreId(event.value);
  };

  /**
   * Set and save the new password for the user
   */
  const saveUserPassword = async () => {
    const validation = passwordValidation(newPassword);

    if (!validation.result) {
      setErrorMessage(validation.errorMessage);
      return;
    }

    try {
      setLoading(true);

      const data = {
        password: newPassword,
      };

      const updatedUser = await updateUserPassword(user.id, data);
      setUser(updatedUser.data.user);
      setStores(updatedUser.data.stores);

      setLoading(false);
      navigateToUsersTable();
    } catch (error) {
      toaster.error(error?.response?.data?.error || THEME_MESSAGES.somethingWrong);
      setLoading(false);
    }
  };

  /**
   * Re-assign the the business the user belongs to
   */
  const reassignBusinessForUser = async () => {
    try {
      setLoading(true);

      const data = {
        businessId: newBusinessId,
      };

      const updatedUser = await assignUserToBusiness(user.id, data);
      setUser(updatedUser?.data?.user);
      setStores(updatedUser?.data?.stores);

      setShowBusinessEditModal(false);
      setLoading(false);
    } catch (error) {
      toaster.error(error?.response?.data?.error || THEME_MESSAGES.somethingWrong);
      setLoading(false);
    }
  };

  /**
   * Save a key-value pair to the User model
   *
   * @param {String} field
   * @param {Any} value
   */

  const updateUser = async (field, value) => {
    try {
      setLoading(true);
      let data = {};

      const apiToUse = field === 'employeeCode' ? editEmployeeCode : updateIndividualValueForUser;

      if (field === 'employeeCode') {
        data = {
          employeeCode: value,
          businessId: user.teamMember?.businessId,
        };
      } else {
        data = {
          field,
          value,
        };
      }

      const userData = await apiToUse(user.id, data);
      setUser(userData.data.user);
      setStores(userData.data.stores);

      setShowUserDetailsModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toaster.error(error?.response?.data?.error || THEME_MESSAGES.somethingWrong);
    }
  };

  /**
   * Render the user's core information
   */
  const renderCoreUserDetails = () => {
    const isInstaller = user.roles.some(({ userType }) => userType === UserTypes.INSTALLER);
    const coreUserDetails = [
      {
        label: 'First Name',
        rawLabel: 'firstname',
        data: user.firstname,
        editable: true,
      },
      {
        label: 'Last Name',
        rawLabel: 'lastname',
        data: user.lastname,
        editable: true,
      },
      {
        label: 'Email',
        rawLabel: 'email',
        data: user.email,
        editable: true,
      },
      {
        label: 'Phone',
        rawLabel: 'phone',
        data: user.phone,
        editable: true,
      },
      {
        label: 'Employee Code',
        rawLabel: 'employeeCode',
        data: user.teamMember?.employeeCode,
        editable: true,
      },
      {
        label: 'Password',
        data: 'Reset Password',
        editable: true,
      },
      {
        label: 'Business',
        data: user.business
          ? user.business.name
          : user.teamMember?.business
          ? user.teamMember?.business?.name
          : '--',
        editable: businessEditable,
      },
      {
        ...(isInstaller && {
          label: 'Store',
          data: user.installerStore?.name ?? '--',
          editable: true,
        }),
      },
    ];

    return (
      <DataCard
        title={'Core Details'}
        cardData={coreUserDetails}
        onEdit={(modalType) => {
          if (modalType === 'Password') {
            setModalType(modalType);
            setShowModal(true);
          } else if (isInstaller && (modalType === 'Business' || modalType === 'Store')) {
            setBusinessToEdit(user?.business?.id);
            setStoreToEdit(user.installerStore?.id);
            setModalType('Business and Store');
            setNewBusinessId(null);
            setNewStoreId(null);
            setShowBusinessAndStoreEditModal(true);
          } else if (modalType === 'Business') {
            setBusinessToEdit(user?.teamMember?.business?.id);
            setModalType(modalType);
            setShowBusinessEditModal(true);
          } else {
            setModalType(modalType);
            setUpdatedFormKey(modalType);
            setShowUserDetailsModal(true);
          }
        }}
      />
    );
  };

  /**
   * Render the stores the user belongs to
   */
  const renderStoreInformation = () => {
    const storeDetails = stores.map((store) => ({
      label: 'Name',
      data: store.name,
    }));

    return <DataCard title={'Store Details'} cardData={storeDetails} />;
  };

  /**
   * Show the edit password modal
   */
  const renderPasswordModal = () => {
    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowModal(false);
          setErrorMessage(null);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={'********'}
          updatedValue={newPassword}
          handleValueChange={(event) => {
            handlePasswordChange(event);
          }}
          inputType="text"
          onSave={() => {
            saveUserPassword();
          }}
          error={errorMessage}
        />
      </Modal>
    );
  };

  /**
   * Show the edit user details modal
   */
  const renderUserDetailsModal = () => {
    const currentValue =
      modalType === 'employeeCode' ? user.teamMember?.employeeCode : user[modalType];
    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowUserDetailsModal(false);
          setUpdatedFormKey(null);
          setUpdatedFormValue(null);
          setErrorMessage(null);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={currentValue}
          updatedValue={updatedFormValue}
          handleValueChange={(event) => {
            handleUserValueChange(event);
          }}
          inputType="text"
          onSave={() => {
            updateUser(updatedFormKey, updatedFormValue);
          }}
          error={errorMessage}
          setError={setErrorMessage}
        />
      </Modal>
    );
  };

  /**
   * Show the edit business modal
   */
  const renderEditBusinessModal = () => {
    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowBusinessEditModal(false);
          setErrorMessage(null);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={businessToEdit != null ? String(businessToEdit) : null}
          updatedValue={newBusinessId}
          handleValueChange={(option) => handleBusinessChange(option?.value ?? null)}
          inputType="select"
          onSave={reassignBusinessForUser}
          selectOptions={businesses}
          selectOnInputChange={businessOnInputChange}
          selectFilterOptions={businessFilterOptions}
          selectLoading={businessesLoading}
          error={errorMessage}
        />
      </Modal>
    );
  };

  const renderEditBusinessAndStoreModal = () => {
    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowBusinessAndStoreEditModal(false);
          setErrorMessage(null);
        }}
      >
        <BusinessAndStoreEditForm
          businessId={businessToEdit}
          updatedBusinessId={newBusinessId}
          handleBusinessChange={handleBusinessChange}
          storeId={storeToEdit}
          updatedStoreId={newStoreId}
          handleStoreChange={handleStoreChange}
          onSave={onSaveInstallerAssignment}
          error={errorMessage}
        />
      </Modal>
    );
  };

  return (
    <>
      {(loading || isAssignInstallerToBusinessAndStoreLoading) && <CentsLoader />}
      {!loading && !isAssignInstallerToBusinessAndStoreLoading && user && (
        <div className="page-container">
          <div className="user-page-container">
            <h1 className="user-page-container-header">
              User: {user.firstname} {user.lastname}
            </h1>
            {renderCoreUserDetails()}
            {stores?.length > 0 && renderStoreInformation()}
          </div>
          {showModal && renderPasswordModal()}
          {showUserDetailsModal && renderUserDetailsModal()}
          {showBusinessEditModal && renderEditBusinessModal()}
          {showBusinessAndStoreEditModal && renderEditBusinessAndStoreModal()}
        </div>
      )}
    </>
  );
};

export default IndividualUserProfile;
